import {
    DataRecordValue,
    DataRecord,
    getColumnLabel,
    CategoricalColorNamespace,
    getNumberFormatter,
} from '@superset-ui/core';
import { EChartsCoreOption, BarSeriesOption } from 'echarts';
import { OptionDataValue } from 'echarts/types/src/util/types';
import { defaultTooltip } from 'plugins/plugin-chart-echarts/src/defaults';
import {
    DEFAULT_FORM_DATA as DEFAULT_CHART_FORM_DATA,
    EchartsHorizontalBarChartProps,
    EchartsHorizontalBarFormData,
    HorizontalBarChartTransformedProps,
} from './types';
import { DEFAULT_LEGEND_FORM_DATA } from '../../../types';
import {
    extractGroupbyLabel,
} from '../../../utils/series';
import { MetricSeriesData, getMetricSeriesData, getSeriesKeys, CONSTANTS } from '../utils';
import { sortKeys } from '../../utils';

// basic  
  
export default function transformProps(
    chartProps: EchartsHorizontalBarChartProps,
): HorizontalBarChartTransformedProps {
    const { formData, height, hooks, queriesData, width, ownState } =
      chartProps;
    const data: DataRecord[] = queriesData[0].data || [];
  
    const {
      groupby,
      metrics,
      combineMetrics,
      colorScheme,
      sliceId,
      showLegend,
      sortLabels,
      numberFormat
    }: EchartsHorizontalBarFormData = {
      ...DEFAULT_LEGEND_FORM_DATA,
      ...DEFAULT_CHART_FORM_DATA,
      ...formData,
    };

    const colorFn = CategoricalColorNamespace.getScale(colorScheme as string);

    const groupbyLabels = Array.isArray(groupby) ? groupby.map(getColumnLabel) : [getColumnLabel(groupby)];
    const keys = data.map(datum =>
      extractGroupbyLabel({ datum, groupby: groupbyLabels, coltypeMapping: {} }),
    );
    const labelMap = data.reduce(
      (acc: Record<string, DataRecordValue[]>, datum) => {
        const label = extractGroupbyLabel({
          datum,
          groupby: groupbyLabels,
          coltypeMapping: {},
        });
        return {
          ...acc,
          [label]: groupbyLabels.map(col => datum[col]),
        };
      },
      {},
    );
  
    const { setDataMask = () => {} } = hooks;
  
    const multiMetricData: MetricSeriesData = getMetricSeriesData(metrics || [], data, combineMetrics);

    const series: BarSeriesOption[] = Object.values(multiMetricData).map(metricData => {
      return {
        name: metricData.name,
        coordinateSystem: 'cartesian2d',
        type: 'bar',
        color: colorFn(metricData.name, sliceId),
        // @ts-ignore
        data: combineMetrics ? ([...metricData.data, metricData.total] as OptionDataValue[])
          : (metricData.data as OptionDataValue[])
      };
    });

    // const categories = combineMetrics ? getSeriesKeys(keys, CONSTANTS.combinedMetricLabel) : keys;

    sortKeys(keys, sortLabels);

    const numberFormatter = getNumberFormatter(numberFormat);
    
    const echartOptions: EChartsCoreOption = {
      grid: {
        // left: '10%',
        bottom: '15%',
        containLabel: true
      },
      legend: {
        show: showLegend,
        bottom: '1%'
      },
      tooltip: {
        ...defaultTooltip,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          hideOverlap: true,
          formatter: numberFormatter
        }
      },
      yAxis: {
        type: 'category',
        axisLabel: {
          show: true,
          ...(!(ownState && ownState.isFullSize) && {
            width: 80,
            overflow: 'truncate'
          })
        },
        data: combineMetrics ? getSeriesKeys(keys, CONSTANTS.combinedMetricLabel) : keys
      },
      series,
    };
  
    return {
      formData,
      width,
      height,
      echartOptions,
      setDataMask,
      labelMap,
      groupby,

      ownState
    };
}
  